import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  styled,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Pagination,
  IconButton,
  PaginationItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  VerifiedUser as ActiveIcon,
  RemoveModerator as InactiveIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { AppDispatch, RootState } from "../../stores";
import {
  getAllUsers,
  toggleUserActive,
  User,
} from "../../features/user-management-module/userAction";
import { clearUserMessages } from "../../features/user-management-module/userSlice";
import UserToggleDialog from "./UserToggleDialog";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "90vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.95)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "85vh",
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 650,
  width: "100%",
  tableLayout: "auto",
  [theme.breakpoints.down("md")]: {
    minWidth: 600,
  },
  [theme.breakpoints.down("sm")]: {
    minWidth: 500,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#4b5b5d",
  fontWeight: 500,
  padding: theme.spacing(0.3),
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: theme.spacing(1),
  },
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: "#f5f0e7",
  fontWeight: 600,
  color: "#4b5b5d",
  padding: theme.spacing(1.2),
  lineHeight: "1rem",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.85rem",
    padding: theme.spacing(2),
    lineHeight: "0.5rem",
  },
}));

const FooterContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  background: "#fff",
  padding: theme.spacing(2),
  zIndex: 1000,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    padding: theme.spacing(1),
  },
}));

const SearchInput = styled(TextField)(({ theme }) => ({
  width: "200px",
  backgroundColor: "#f8f8f8",
  borderRadius: 5,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
  [theme.breakpoints.down("md")]: {
    width: "200px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

// Add the LoaderContainer and LoaderText styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  minHeight: "300px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

interface TableUser {
  no: number;
  name: string;
  email: string;
  role: string;
  subscription: string;
  siteLimits: string;
  fleetLimits: string;
  dateOfJoining: string;
  id: string;
  isActive: boolean;
}

const UserManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { users, loading, error, totalPages } = useSelector(
    (state: RootState) => state.users
  );

  const [tableUsers, setTableUsers] = useState<TableUser[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(getAllUsers({ page, limit: pageSize, role: "customer" }));
    return () => {
      dispatch(clearUserMessages());
    };
  }, [dispatch, page, pageSize]);

  useEffect(() => {
    const mappedUsers: TableUser[] = users.map((user: User, index: number) => ({
      no: (page - 1) * pageSize + index + 1,
      name: `${user.firstname} ${user.lastname}`,
      email: user.email,
      role: user.role.rolename,
      subscription: user.subscription?.planName || "No Subscription",
      siteLimits: user.subscription
        ? `${user.subscription.siteQuotaUsed}/${user.subscription.siteQuotaLimit}`
        : "N/A",
      fleetLimits: user.subscription
        ? `${user.subscription.fleetQuotaUsed}/${user.subscription.fleetQuotaLimit}`
        : "N/A",
      dateOfJoining: user.id
        ? new Date(
            parseInt(user.id.substring(0, 8), 16) * 1000
          ).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
          })
        : "N/A",
      id: user.id,
      isActive: user.isActive,
    }));
    setTableUsers(mappedUsers);
  }, [users, page, pageSize]);

  const filteredUsers = tableUsers.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value);
    setPage(1);
  };

  const handleView = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    navigate(`/main/user-management/view-user/${userId}`, { state: { user } });
  };

  const handleEdit = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    navigate(`/main/user-management/edit-user/${userId}`, { state: { user } });
  };

  const handleToggleActive = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setToggleDialogOpen(true);
    }
  };

  const handleToggleConfirm = async () => {
    if (selectedUser) {
      await dispatch(toggleUserActive(selectedUser.id)).then(() => {
        setToggleDialogOpen(false);
        setSelectedUser(null);
      });
      await dispatch(getAllUsers({ page, limit: pageSize, role: "customer" }));
    }
  };

  const totalUsers = totalPages * pageSize;

  const renderPaginationItems = (item: any) => {
    const { page: itemPage, type } = item;
    if (type === "previous" || type === "next") {
      return <PaginationItem {...item} />;
    }
    if (itemPage === page || itemPage === page + 1) {
      return <PaginationItem {...item} />;
    }
    return null;
  };

  return (
    <Container>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          maxWidth: "auto",
          margin: "0 auto",
          paddingBottom: "80px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight={600}
            color="#4b5b5d"
          >
            User Management
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(2),
              [theme.breakpoints.down("sm")]: {
                width: "100%",
                flexDirection: "column",
              },
            }}
          >
            <SearchInput
              placeholder="Search users..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        {/* Enhanced Loading State */}
        {loading && (
          <LoaderContainer>
            <CircularProgress
              size={40}
              thickness={4}
              sx={{
                color: "#4b5b5d",
              }}
            />
            <LoaderText>Loading users data...</LoaderText>
          </LoaderContainer>
        )}

        {/* Error State */}
        {!loading && error && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 2,
              minHeight: "200px",
            }}
          >
            <Typography
              color="error"
              sx={{
                textAlign: "center",
                fontSize: "0.9rem",
                fontWeight: 500,
              }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {/* Success State */}
        {!loading && !error && (
          <Box
            sx={{
              overflowX: "auto",
              "-webkit-overflow-scrolling": "touch",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#4b5b5d",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#354142",
              },
            }}
          >
            <StyledTable size={isMobile ? "small" : "medium"}>
              <TableHead sx={{ background: "#f5f0e7" }}>
                <TableRow>
                  <StyledTableHeadCell>No.</StyledTableHeadCell>
                  <StyledTableHeadCell>Name</StyledTableHeadCell>
                  <StyledTableHeadCell>Email</StyledTableHeadCell>
                  <StyledTableHeadCell>Role</StyledTableHeadCell>
                  <StyledTableHeadCell>Subscription</StyledTableHeadCell>
                  <StyledTableHeadCell>Site Report Limits</StyledTableHeadCell>
                  <StyledTableHeadCell>Fleet Report Limits</StyledTableHeadCell>
                  <StyledTableHeadCell>Date Of Joining</StyledTableHeadCell>
                  <StyledTableHeadCell>Actions</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 && !error ? (
                  <TableRow>
                    <StyledTableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="h6" color="#4b5b5d" fontWeight={500}>
                        No Users Available
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#f8f8f8" },
                        "&:hover": { backgroundColor: "#e0e0e0" },
                      }}
                    >
                      <StyledTableCell>{user.no}.</StyledTableCell>
                      <StyledTableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {user.name}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>{user.email}</StyledTableCell>
                      <StyledTableCell>{user.role}</StyledTableCell>
                      <StyledTableCell>{user.subscription}</StyledTableCell>
                      <StyledTableCell>{user.siteLimits}</StyledTableCell>
                      <StyledTableCell>{user.fleetLimits}</StyledTableCell>
                      <StyledTableCell>{user.dateOfJoining}</StyledTableCell>
                      <StyledTableCell>
                        <Box
                          sx={{
                            display: "flex",
                            gap: isMobile ? 1 : 0,
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            onClick={() => handleView(user.id)}
                            sx={{ color: "#4b5b5d" }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            onClick={() => handleEdit(user.id)}
                            sx={{ color: "#4b5b5d" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            onClick={() => handleToggleActive(user.id)}
                          >
                            {user.isActive ? (
                              <ActiveIcon fontSize="small" />
                            ) : (
                              <InactiveIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </StyledTable>
          </Box>
        )}

        <UserToggleDialog
          open={toggleDialogOpen}
          onClose={() => setToggleDialogOpen(false)}
          selectedUser={selectedUser}
          handleDeleteConfirm={handleToggleConfirm}
        />

        {!loading && (
          <FooterContainer>
            <Typography
              variant="body2"
              color="#4b5b5d"
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Showing {(page - 1) * pageSize + 1} to{" "}
              {Math.min(page * pageSize, totalUsers)} of {totalUsers} entries
            </Typography>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="#4b5b5d">
                  Show:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "4px 8px",
                        fontSize: "0.8rem",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="#4b5b5d">
                  entries
                </Typography>
              </Box>
            )}

            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              renderItem={renderPaginationItems}
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                width: isMobile ? "100%" : "auto",
                "& .MuiPaginationItem-root": {
                  color: "#4b5b5d",
                  "&.Mui-selected": {
                    backgroundColor: "#758d88",
                    color: "#f8f9fa",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#4b5b5d",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "rgba(75, 91, 93, 0.04)",
                  },
                },
              }}
            />
          </FooterContainer>
        )}
      </motion.div>
    </Container>
  );
};

export default UserManagement;
